import React, { useState } from 'react';

import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';

// Which row (if any) the list is currently editing: adding a new one, or editing an existing row.
export type Editing<Row> = { mode: 'add' } | { mode: 'edit', row: Row }

// The lifted editor card used inside a MappingList. It gives a piece of UI *dialog semantics* —
// a local buffer committed only on Spara, discarded on Avbryt — but rendered in place. It sits at
// zIndex 1201, just above the list's Backdrop scrim (1200), so it lifts out of the greyed-out page
// while staying exactly where the user was looking. Caller fills the fields as `children`.
export function InPlaceEditor({ disabled, canSave, onCancel, onSave, children }: {
    disabled: boolean, canSave: boolean, onCancel: () => void, onSave: () => void, children: React.ReactNode,
}) {
    return (
        <Paper elevation={8} sx={{ position: 'relative', zIndex: 1201, p: 1.5, m: 0.5, width: '48rem', maxWidth: '100%', display: 'flex', flexFlow: 'column', gap: 1 }}>
            {children}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <Button size="small" disabled={disabled} onClick={onCancel}>Avbryt</Button>
                <Button size="small" variant="contained" disabled={disabled || !canSave} onClick={onSave}>Spara</Button>
            </div>
        </Paper>
    )
}

function MappingRow({ name, summary, unmappedText, editLabel, actions, onEdit, onRemove, canEdit, editDisabledReason, disabled }: {
    name: React.ReactNode, summary: string | null, unmappedText: string, editLabel: string,
    actions: React.ReactNode, onEdit: () => void, onRemove: (() => void) | null, canEdit: boolean,
    editDisabledReason: string, disabled: boolean,
}) {
    // `canEdit` false ⇒ still shown (with its name), but Ändra/Ta bort are disabled so you can't
    // open an editor whose save the backend refuses. The reason comes from the caller: scope is the
    // usual one, but a missing capability disables the same buttons for a quite different reason,
    // and a tooltip that names the wrong one sends people looking in the wrong place.
    // A disabled button emits no hover events, so the explanatory tooltip wraps a <span>.
    const editTitle = canEdit ? '' : editDisabledReason
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem 0.75rem', borderTop: '1px solid #eee' }}>
            <div style={{ flex: '0 0 13rem', fontWeight: 500 }}>{name}</div>
            <div style={{ flex: 1, fontSize: '0.9em', color: summary === null ? '#b26a00' : '#555' }}>{summary ?? unmappedText}</div>
            {actions}
            <Tooltip title={editTitle}><span>
                <Button size="small" disabled={disabled || !canEdit} onClick={onEdit}>{editLabel}</Button>
            </span></Tooltip>
            {onRemove !== null && <Tooltip title={editTitle}><span>
                <Button size="small" color="error" disabled={disabled || !canEdit} onClick={onRemove}>Ta bort</Button>
            </span></Tooltip>}
        </div>
    )
}

// A WYSIWYG list of saved mappings edited via an in-place dialog (the pattern used by both Swish
// settings tables). Every visible row is a saved value; Ändra / Lägg till open a greyed in-place
// editor (a Backdrop makes the rest of the page inert, so nothing reads as unsaved); Ta bort is a
// direct, auto-applied action. Generic over the row type — the caller supplies how to key/name a
// row, its saved location summary (null = unmapped), the editor UI, and the persistence.
export default function MappingList<Row>({
    rows, getKey, renderName, getSummary, unmappedText = 'ej mappad', unmappedActionLabel = 'Mappa',
    nameHeader, locationHeader, canRemove, onRemove, addLabel, renderEditor, renderRowActions, canEdit,
    editDisabledReason = 'Utanför ditt ansvarsområde', disabled, emptyText,
}: {
    rows: Row[],
    getKey: (row: Row) => string,
    renderName: (row: Row) => React.ReactNode,
    getSummary: (row: Row) => string | null,
    unmappedText?: string | ((row: Row) => string),         // shown when a row has no location (e.g. "ej mappad")
    unmappedActionLabel?: string | ((row: Row) => string),  // edit-button label for an unmapped row ("Mappa" / "Sätt")
    nameHeader: string,                      // column label over the name (e.g. "Kategori")
    locationHeader: string,                  // column label over the location summary (e.g. "Bokförs på")
    canRemove: (row: Row) => boolean,
    onRemove: (row: Row) => void,
    addLabel: string | null,                 // null = this list can't add rows (e.g. fixed categories)
    renderEditor: (editing: Editing<Row>, close: () => void) => React.ReactNode,
    renderRowActions?: (row: Row) => React.ReactNode,   // extra per-row buttons before Ändra/Ta bort
    canEdit?: (row: Row) => boolean,         // false ⇒ shown but Ändra/Ta bort disabled
    editDisabledReason?: string | ((row: Row) => string),   // why, in the tooltip (default: out of scope)
    disabled: boolean,
    emptyText?: string,
}) {
    const [editing, setEditing] = useState<Editing<Row> | null>(null)
    const close = () => setEditing(null)
    return (
        <>
            {/* The scrim: greys out and blocks the rest of the page while an editor is open, so it
                reads as a dialog. Click-away = cancel (unless a save is in flight). */}
            {editing !== null &&
                <Backdrop open sx={{ zIndex: 1200 }} onClick={() => { if (!disabled) close() }}/>}
            <Paper variant="outlined">
                <div style={{ display: 'flex', gap: '1rem', padding: '0.4rem 0.75rem', fontWeight: 600, fontSize: '0.8em', color: '#555' }}>
                    <div style={{ flex: '0 0 13rem' }}>{nameHeader}</div>
                    <div style={{ flex: 1 }}>{locationHeader}</div>
                </div>
                {rows.length === 0 && editing === null && emptyText !== undefined &&
                    <div style={{ padding: '0.5rem 0.75rem', color: '#777' }}>{emptyText}</div>}
                {rows.map(row => {
                    if (editing !== null && editing.mode === 'edit' && getKey(editing.row) === getKey(row))
                        return <React.Fragment key={getKey(row)}>{renderEditor(editing, close)}</React.Fragment>
                    const summary = getSummary(row)
                    const resolve = (v: string | ((r: Row) => string)) => typeof v === 'function' ? v(row) : v
                    return <MappingRow key={getKey(row)} name={renderName(row)}
                        summary={summary} unmappedText={resolve(unmappedText)}
                        editLabel={summary === null ? resolve(unmappedActionLabel) : 'Ändra'}
                        actions={renderRowActions?.(row)}
                        onEdit={() => setEditing({ mode: 'edit', row })}
                        onRemove={canRemove(row) ? () => onRemove(row) : null}
                        canEdit={canEdit?.(row) ?? true}
                        editDisabledReason={resolve(editDisabledReason)}
                        disabled={disabled}/>
                })}
                {addLabel !== null && (editing?.mode === 'add'
                    ? renderEditor({ mode: 'add' }, close)
                    : <div style={{ padding: '0.5rem 0.75rem', borderTop: rows.length > 0 ? '1px solid #eee' : undefined }}>
                        <Button size="small" disabled={disabled || editing !== null} onClick={() => setEditing({ mode: 'add' })}>{addLabel}</Button>
                      </div>)}
            </Paper>
        </>
    )
}
